// src/components/UserMeet/User-Meet-Private/BreakoutActiveModal.jsx
import React from "react";
import {
  X,
  Users,
  ArrowRight,
  StopCircle,
  AlertTriangle,
  Clock,
} from "lucide-react";
import BreakoutCountdownTimer from "./BreakoutCountdownTimer";

export default function BreakoutActiveModal({
  open,
  onClose,
  onRejoin,
  onEnd,
  breakoutData,
  isOwner,
  ending,
  rejoining,
}) {
  if (!open || !breakoutData) return null;

  const loading = ending || rejoining;

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-[10000] p-5 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl max-w-lg w-full border border-purple-500/15 animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/15 to-violet-500/15 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">
                Active Breakout Room
              </h2>
              <p className="text-sm text-white/60">
                You have a breakout room currently in progress
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-400 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Status Card */}
          <div className="p-4 bg-gradient-to-br from-purple-500/8 to-violet-500/8 border border-purple-500/20 rounded-xl">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Left: Status */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <div className="absolute inset-0 w-3 h-3 rounded-full bg-green-400 animate-ping opacity-75" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    Breakout is Live
                  </p>
                  <p className="text-xs text-white/50">
                    {isOwner ? "You are the owner" : "You are a participant"}
                  </p>
                </div>
              </div>

              {/* Right: Timer */}
              {breakoutData.expiresAt && (
                <BreakoutCountdownTimer
                  expiresAt={breakoutData.expiresAt}
                  onExpire={() => {}}
                />
              )}
            </div>

            {/* Duration info */}
            {breakoutData.timeLimitMinutes && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-purple-500/15">
                <Clock size={14} className="text-purple-400" />
                <span className="text-xs text-white/50">
                  Total duration: {breakoutData.timeLimitMinutes} minutes
                </span>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-white/40 uppercase tracking-wide">
              What would you like to do?
            </p>

            {/* Rejoin Option */}
            <button
              onClick={onRejoin}
              disabled={loading}
              type="button"
              className="w-full flex items-center gap-4 p-4 bg-gradient-to-br from-green-500/8 to-emerald-500/8 border border-green-500/20 rounded-xl hover:from-green-500/15 hover:to-emerald-500/15 hover:border-green-500/35 transition-all group disabled:opacity-50 disabled:cursor-not-allowed text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-green-500/15 border border-green-500/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                {rejoining ? (
                  <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin" />
                ) : (
                  <ArrowRight size={18} className="text-green-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-green-400">
                  {rejoining ? "Rejoining..." : "Rejoin Breakout Room"}
                </p>
                <p className="text-xs text-white/50 mt-0.5">
                  Go back to the private breakout conversation
                </p>
              </div>
            </button>

            {/* End Option (owner only) */}
            {isOwner && (
              <button
                onClick={onEnd}
                disabled={loading}
                type="button"
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-br from-red-500/8 to-rose-500/8 border border-red-500/20 rounded-xl hover:from-red-500/15 hover:to-rose-500/15 hover:border-red-500/35 transition-all group disabled:opacity-50 disabled:cursor-not-allowed text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  {ending ? (
                    <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                  ) : (
                    <StopCircle size={18} className="text-red-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-red-400">
                    {ending
                      ? "Ending..."
                      : "End Breakout & Create New"}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5">
                    Close the current breakout for everyone and start a new one
                  </p>
                </div>
              </button>
            )}

            {/* Non-owner info */}
            {!isOwner && (
              <div className="flex gap-3 p-3 bg-yellow-500/8 border border-yellow-500/20 rounded-lg">
                <AlertTriangle
                  size={16}
                  className="text-yellow-400 flex-shrink-0 mt-0.5"
                />
                <p className="text-xs text-white/60">
                  Only the breakout owner can end the room. You can rejoin or
                  wait for it to end.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={loading}
            type="button"
            className="w-full px-5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white/80 font-semibold text-sm hover:bg-white/10 hover:border-white/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Stay in Main Meeting
          </button>
        </div>
      </div>
    </div>
  );
}